import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import Keycloak from 'keycloak-js';

/** The token claims this page reads. Declared locally so the page does
 * not depend on how keycloak-js happens to type its optional claims. */
type TokenClaims = {
  preferred_username?: string;
  email?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
};

/**
 * Access-denied page at /forbidden.
 *
 * This page exists so that a route guard can deny VISIBLY. Until it was
 * added, the app's only role guard returned `parseUrl('/forbidden')`,
 * nothing was registered at that path, and `{ path: '**', redirectTo: '' }`
 * swallowed the redirect — so being refused looked identical to a dead
 * link: you clicked a session, silently stayed on the home page, and got
 * no message, no toast and nothing in the console. On the mocked env,
 * where a Keycloak realm-import artifact stripped the role the guard was
 * checking, that cost hours to diagnose.
 *
 * So this page states the case instead of apologising: which URL was
 * refused, why, who the caller is, and — since a missing role is the
 * usual cause — which roles the token actually carries. Everything comes
 * from the URL plus the caller's own token, so the page is deep-linkable
 * and safe to paste into a ticket.
 */
@Component({
  selector: 'app-forbidden',
  imports: [CommonModule, RouterLink],
  templateUrl: './forbidden.html',
})
export class Forbidden {
  private readonly route = inject(ActivatedRoute);
  private readonly keycloak = inject(Keycloak);

  /** Why the guard refused: 'unauthenticated' when there is no session. */
  readonly reason = this.route.snapshot.queryParamMap.get('reason') ?? 'denied';

  /** URL that was refused, so the page can offer to retry it. */
  readonly from = this.route.snapshot.queryParamMap.get('from') ?? '/';

  /** Role the route demanded, when a guard was checking for one. */
  readonly requiredRole = this.route.snapshot.queryParamMap.get('required');

  private get claims(): TokenClaims {
    return (this.keycloak.tokenParsed as TokenClaims) ?? {};
  }

  get signedIn(): boolean {
    return !!this.keycloak.authenticated;
  }

  /** Who the token says we are, for the "checked as" line. */
  get identity(): string {
    return this.claims.email ?? this.claims.preferred_username ?? 'unknown';
  }

  get realm(): string {
    return this.keycloak.realm ?? 'unknown';
  }

  get realmRoles(): string[] {
    return [...(this.claims.realm_access?.roles ?? [])].sort();
  }

  /** Resource (client) roles, flattened to "client: a, b" lines. Empty is
   * itself the diagnosis: a guard checking resource roles refuses
   * everything when the token carries none. */
  get resourceRoles(): { client: string; roles: string }[] {
    const access = this.claims.resource_access ?? {};

    return Object.keys(access)
      .sort()
      .map(client => ({
        client,
        roles: [...(access[client]?.roles ?? [])].sort().join(', '),
      }))
      .filter(entry => entry.roles.length > 0);
  }

  /** Send the caller through Keycloak and back to what they wanted. */
  signIn(): void {
    void this.keycloak.login({ redirectUri: window.location.origin + this.from });
  }
}
